import { useEffect, useState } from "react";
import { useSVG } from "../SVGContext";
import { GRADIENTS } from "../values/Gradients";
import MySketchPicker, { customSwatches } from "../MySketchPicker";
import { Dialog, Slider, TextField, Tooltip } from "@mui/material";
import { DEFTYPES } from "../values/enums";
import { BGDEF, ROOT_GROUP_ID } from "../values/constants";
import AddIcon from "@mui/icons-material/Add";
import { toRgb } from "./BrandPanel";
import EditIcon from "@mui/icons-material/Edit";
import { RandomizeColors } from "../topbar/ColorRandomizer";

export const BACKGROUND_DEF_SUFFIX = "background";

export function BackgroundColorPanel({}) {
  const { items, defs, update, width, height, groups, selectedIds } = useSVG();
  const [itemId, setItemId] = useState(selectedIds[0] || "background");

  useEffect(() => {
    setItemId(selectedIds[0] || "background");
  }, [selectedIds]);

  function onClick(colors) {
    const item = items[itemId];
    const defId = item.id + "-" + BACKGROUND_DEF_SUFFIX;
    let def = item.defs?.find((def) => def.id === defId);
    if (!def) {
      def = {
        id: defId,
        type: DEFTYPES.LINEARGRADIENT,
        colors: [...colors],
        gradientUnits: "objectBoundingBox",
        x1: "0%",
        y1: "50%",
        x2: "100%",
        y2: "50%",
      };
      item.defs = item.defs || [];
      item.defs.push(def);
    } else {
      def.x1 = "0%";
      def.y1 = "50%";
      def.x2 = "100%";
      def.y2 = "50%";
      def.colors = [...colors];
    }
    item.fill = 'url(#' + defId + ')';
    items[itemId] = item;
    update({ items: { ...items }, defs, groups });

    // copy the color to clipboard
    // navigator.clipboard.writeText(colors[0]);
  }

  return (
    <div
      style={{
        padding: 10,
      }}
    >
      <RandomizeColors />

      <DesignColors onClick={onClick} />
      <BrandColors onClick={onClick} />
      <Solids onClick={onClick} />
      <Gradients onClick={onClick} />
    </div>
  );
}

const subtitle = {
  fontSize: "0.8rem",
  fontWeight: "bold",
  margin: "10px 0",
};

const colorsHolder = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "start",
  alignItems: "start",
  gap: 15,
};

function DesignColors({ onClick }) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState(0);
  const [shouldDuplicateIndex, setShouldDuplicateIndex] = useState(-1);
  const { design, setDesign } = useSVG();

  return (
    <>
      <div style={subtitle}>Design Colors</div>
      <div style={colorsHolder}>
        <ColorCircle
          background={"white"}
          onClick={() => {
            setShowColorPicker(true);
          }}
          name={"New"}
          icon={<AddIcon style={{ color: "#333" }} />}
        />
        {design.colors?.map((color, index) => (
          <div
            key={index}
            className={selectedColor === index ? "edit-color-icon-wrapper" : ""}
            style={{
              position: "relative",
              // width: "100%",
              // height: "100%",
            }}
          >
            <ColorCircle
              background={color}
              isSelected={selectedColor === index}
              onClick={() => {
                setSelectedColor(index);
                onClick([color, color]); // two are needed for image-magick serverside conversion to work
              }}
              name={color}
            />
            <EditIcon
              className="edit-color-icon"
              style={{
                position: "absolute",
                display: "none",

                top: "25%",
                left: "25%",
                color: "#333",
                width: "50%",
                height: "50%",
                cursor: "pointer",
              }}
              onClick={() => {
                // duplicate the color
                setShouldDuplicateIndex(index);
                setSelectedColor(index);
                setShowColorPicker(true);
              }}
            />
          </div>
        ))}
      </div>
      <Dialog open={showColorPicker} onClose={() => setShowColorPicker(false)}>
        <MySketchPicker
          colors={"red"}
          onChangeComplete={(clr) => {
            if (shouldDuplicateIndex != -1) {
              design.colors.splice(shouldDuplicateIndex, 0, clr.hex);
              setShouldDuplicateIndex(-1);
            }
            design.colors[selectedColor] = clr.hex;
            setDesign({ ...design });

            onClick([clr.hex]);
            // setShowColorPicker(false);
          }}
          addClickAway={false}
          setShowPicker={setShowColorPicker}
        />
      </Dialog>
    </>
  );
}

function BrandColors({ onClick }) {
  const { brand } = useSVG();
  if (!brand) return null;
  return (
    <>
      <div style={subtitle}>Brand Colors</div>
      {brand.colors?.map((palette, i) => (
        <div style={colorsHolder} key={i}>
          {palette.colors?.map((color, j) => {
            const c = toRgb(color);
            return <ColorCircle key={j} background={c} onClick={() => onClick([c, c])} name={c} />;
          })}
        </div>
      ))}
    </>
  );
}

function Solids({ onClick }) {
  const solids = customSwatches;

  return (
    <>
      <div style={subtitle}>Solids</div>
      <div style={colorsHolder}>
        {solids.map((color, index) => (
          <ColorCircle key={index} background={color} onClick={() => onClick([color, color])} name={color} />
        ))}
      </div>
    </>
  );
}

function computeGradientLine(angleDeg) {
  const angle = (angleDeg * Math.PI) / 180;
  const x1 = 0;
  const y1 = 50;
  const x2 = x1 + 100 * Math.cos(angle);
  const y2 = y1 + 100 * Math.sin(angle);
  return { x1: x1 + "%", y1: y1 + "%", x2: x2 + "%", y2: y2 + "%" };
}

function Gradients({ onClick }) {
  const { items, selectedIds, update } = useSVG();
  const [showAll, setShowAll] = useState(false);
  const grads = showAll ? GRADIENTS : GRADIENTS.slice(0, 6);
  const [angle, setAngle] = useState(0);

  useEffect(() => {
    const ids = selectedIds.length === 0 ? ["background"] : selectedIds;
    for (let id of ids) {
      const item = items[id];
      const def = item.defs?.find((def) => def.id == item.id + "-" + BACKGROUND_DEF_SUFFIX);
      if (!def) return;
      const { x1, y1, x2, y2 } = computeGradientLine(angle);
      def.x1 = x1;
      def.y1 = y1;
      def.x2 = x2;
      def.y2 = y2;
      update({ items });
    }
  }, [angle]);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 20,
        }}
      >
        <div style={subtitle}>Gradients</div>
        <div
          onClick={() => setShowAll(!showAll)}
          style={{
            cursor: "pointer",
            fontSize: "0.8rem",
          }}
        >
          See {showAll ? "Less" : "All"}
        </div>
      </div>
      <div
        style={{
          margin: "0px 10px 20px 10px",
        }}
      >
        <Slider fullWidth value={angle} onChange={(e, val) => setAngle(val)} />
      </div>

      <div style={colorsHolder}>
        {grads.map((grad, index) => (
          <ColorCircle
            key={index}
            background={`linear-gradient(to right, ${grad.colors.join(",")})`}
            onClick={() => {
              onClick(grad.colors);
            }}
            name={grad.name}
          />
        ))}
      </div>
    </>
  );
}

export function ColorCircle({ background, onClick, name, icon, isSelected }) {
  return (
    <Tooltip title={name || background} placement="bottom">
      <div
        style={{
          background,
          borderRadius: "50%",
          border: "solid 3px #ddd",
          borderStyle: "solid",
          borderWidth: 3,
          borderColor: isSelected ? "white" : "#222",
          width: 40,
          height: 40,
          cursor: "pointer",
          // border: background == "transparent" ? "solid 1px #ddd" : "none",

          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        onClick={onClick}
      >
        {icon}
      </div>
    </Tooltip>
  );
}
